import React from 'react';
import AvatarFilled from '../../../assets/icons/svg/AvatarFilled';
import AvatarOutlined from '../../../assets/icons/svg/AvatarOutlined';
import ChevronLeft from '../../../assets/icons/svg/ChevronLeft';
import ChevronRight from '../../../assets/icons/svg/ChevronRight';
import Close from '../../../assets/icons/svg/Close';
import Play from '../../../assets/icons/svg/Play';
import PlaySmall from '../../../assets/icons/svg/PlaySmall';
import PlayLarge from '../../../assets/icons/svg/PlayLarge';
import favoriteWhite from '../../../assets/animations/favourite-white.json';
import favorite from '../../../assets/animations/favorite_v2.json';
import unfavoriteWhite from '../../../assets/animations/unfavourite-white.json';
import unfavorite from '../../../assets/animations/unfavorite_v2.json';
import type { IconProps } from '../Icon/Icon';
import type { IconButtonProps, Variant } from './IconButton';
import Favorite from '../../../assets/icons/svg/Favorite';
import LazyLottieAnimation from '../Animation/LazyLottieAnimation';

function getIconSize(size: IconProps['size'], variant: Variant): IconProps['size'] {
  if (variant === 'seat') {
    return size === 'large' ? 'large' : 'medium';
  }

  if (variant === 'left' || variant === 'right' || variant === 'close') return 'large';

  if (size === 'small') return 'small';

  return size;
}

function getPadding(size: string, variant: Variant) {
  if (variant === 'favorite-animation') return 'p-0';
  if (variant === 'seat') {
    return size === 'large' ? 'sm:p-16' : 'p-4';
  }

  if (variant === 'left' || variant === 'right' || variant === 'close') return 'p-0';

  if (size === 'small') return 'p-10';

  if (size === 'medium') return 'p-12';

  return 'p-12 sm:p-16';
}

function getIconAnimationData(checked?: boolean, hideBg?: boolean) {
  return {
    animationData: hideBg ? (checked ? favorite : unfavorite) : (checked ? favoriteWhite : unfavoriteWhite),
    staticAnimationData: hideBg ? unfavorite : unfavoriteWhite
  };
}

export const useIconButtonVariant = <V extends Variant>({
  variant,
  size = 'medium',
  hideBackground,
  checked,
  disabled,
}: Omit<IconButtonProps<V>, 'onAction'>) => {
  let icon;
  let style = '';

  const iconSize = getIconSize(size, variant);
  const iconPadding = getPadding(size, variant);
  const { animationData } = getIconAnimationData(
    checked,
    hideBackground
  );

  const navigationStyle = hideBackground
    ? 'bg-transparent stroke-neutrals-white'
    : 'bg-neutrals-coolGrey-300 stroke-neutrals-white is-desktop-detected:hover:bg-neutrals-grey-200 is-desktop-detected:hover:stroke-neutrals-grey-950';

  switch (variant) {
  case 'play':
    style = 'bg-purple-primary is-desktop-detected:hover:bg-purple-light fill-neutrals-white text-transparent';
    icon = size === 'large' ? <PlayLarge /> : size === 'small' ? <PlaySmall /> : <Play />;
    break;
  case 'favorite-logo-no-bg':
    // eslint-disable-next-line no-case-declarations
    const buttonIconSizeX = size === 'large' ? 'size-32' : size === 'medium' ? 'size-24' : 'size-16';
    style = `relative disabled:cursor-not-allowed bg-transparent text-transparent !focus-within:ring-inset *:relative *:stroke-gold-primary ${checked ? '*:fill-gold-primary' : ''} ${buttonIconSizeX}`;
    icon = <Favorite />;
    break;
  case 'favorite-logo':
    // eslint-disable-next-line no-case-declarations
    const bgIconSize = size === 'large' ? 'before:size-56 before:sm:size-64' : size === 'medium' ? 'before:size-48' : 'before:size-32';
    // eslint-disable-next-line no-case-declarations
    const buttonIconSize = size === 'large' ? 'size-32' : size === 'medium' ? 'size-24' : 'size-16';
    // eslint-disable-next-line no-case-declarations
    const bgIconStyle = `before:absolute before:left-0 before:top-0 before:bg-gold-primary before:rounded-[99999px] *:relative *:stroke-white ${checked ? '*:fill-white' : ''} is-desktop-detected:hover:before:bg-gold-medium disabled:bg-neutrals-coolGrey-300 ${bgIconSize}`;
    style = `relative disabled:cursor-not-allowed bg-transparent text-transparent !focus-within:ring-inset ${buttonIconSize} ${bgIconStyle}`;
    icon = <Favorite />;
    break;
  case 'favorite-animation':
    // eslint-disable-next-line no-case-declarations
    const buttonSize = size === 'large' ? 'size-56 sm:size-64' : size === 'medium' ? 'size-48' : 'size-32';
    // eslint-disable-next-line no-case-declarations
    const bgSize = size === 'large' ? 'before:size-56 before:sm:size-64' : size === 'medium' ? 'before:size-48' : 'before:size-32';
    // eslint-disable-next-line no-case-declarations
    const bgStyle = `before:absolute before:left-0 before:top-0 before:bg-gold-primary before:rounded-[99999px] is-desktop-detected:hover:before:bg-gold-medium disabled:bg-neutrals-coolGrey-300 ${bgSize}`;
    // eslint-disable-next-line no-case-declarations
    const animationTranslateStyle = size === 'large' ? 'absolute' : 'absolute -translate-x-8 -translate-y-8';
    // eslint-disable-next-line no-case-declarations
    const animationContainerStyle = `pointer-events-none ${size === 'small' ? 'size-48' : size === 'medium' ? 'size-64' : 'size-56 sm:size-64'} left-0 top-0 ${!hideBackground ? animationTranslateStyle : '!h-[7.2rem] !w-[7.2rem]'}`;

    style = `relative disabled:cursor-not-allowed bg-transparent text-transparent !focus-within:ring-inset ${buttonSize} ${!hideBackground ? bgStyle : '!h-auto !w-auto'}`;
    icon = (
      <LazyLottieAnimation
        animationData={animationData}
        goToEnd={!checked ? true : false}
        className={animationContainerStyle}
      />
    );
    break;
  case 'seat':
    style =
        'bg-gold-primary is-desktop-detected:hover:bg-gold-medium fill-neutrals-white disabled:bg-neutrals-coolGrey-300 disabled:cursor-not-allowed';
    icon = disabled ? <AvatarFilled /> : <AvatarOutlined />;
    break;
  case 'right':
    style = navigationStyle;
    icon = <ChevronRight />;
    break;
  case 'left':
    style = navigationStyle;
    icon = <ChevronLeft />;
    break;
  case 'close':
    style = navigationStyle;
    icon = <Close />;
    break;
  default:
    icon = <Play />;
    break;
  }

  return { icon, style, iconSize, iconPadding };
};
